import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
    Chip,
    Card,
    CardContent,
    InputAdornment
} from '@mui/material';
import {
    Send as SendIcon,
    Draw as DrawIcon,
    Clear as ClearIcon,
    ArrowBack as ArrowBackIcon,
    PersonSearch as PersonSearchIcon,
    Mic as MicIcon,
    MicOff as MicOffIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { OPERATOR_PRACTICAL_EVALUATION_STORE, JOB_STORE, EMPLOYEE_STORE } from '../../../config/constants';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import { generateUUID } from '../../../utils/uuid';
import Swal from 'sweetalert2';
import operatorPracticalEvaluationService from '../../../services/operatorPracticalEvaluationService';
import SelectEmployeesAndTempsModal from '../../jha/SelectEmployeesAndTempsModal';
import useDictation from '../../../hooks/useDictation';

const STATUS_DRAFT = 'draft';
const STATUS_SUBMITTED = 'submitted';

const OperatorPracticalEvaluationForm = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const uuid = searchParams.get('uuid');

    // Check if opened from Training Due
    const selectedTrainingDue = location.state?.selectedTrainingDue;
    const isFromTrainingDue = !!selectedTrainingDue;
    const operatorNameFromTrainingDue = selectedTrainingDue?.fullName || '';
    const operatorIdFromTrainingDue = selectedTrainingDue?.employeeId || '';
    const courseFromTrainingDue = selectedTrainingDue?.course || '';
    const trainingDueId = selectedTrainingDue?.id || null;

    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const [employees, setEmployees] = useState([]);
    const [form, setForm] = useState({
        uuid: uuid || generateUUID(),
        trainingDueId: trainingDueId,
        course: courseFromTrainingDue,
        operatorId: operatorIdFromTrainingDue || '',
        operatorName: operatorNameFromTrainingDue || '',
        evaluatorId: userId,
        evaluatorName: userFullName,
        date: dayjs().format('YYYY-MM-DD'),
        equipmentOperated: isFromTrainingDue ? 'Forklift' : '',

        preUseInspection: {
            followChecklist: null,
            lookForDamage: null,
            documentFindings: null
        },
        pickingUpTheLoad: {
            squareUpCenter: null,
            stopOneFootFromLoad: null,
            clearPersonnel: null,
            levelForks: null,
            tiltMastBack: null,
            tiltBackStabilize: null,
            lookOverShoulders: null,
            lowerToTravelHeight: null
        },
        traveling: {
            doNotRaiseLower: null,
            maintainSpeed: null,
            observeRules: null,
            keepArmsLegs: null,
            followDistance: null,
            slowDownCornering: null,
            useHorn: null,
            travelFacingUphill: null,
            stopSmoothly: null
        },
        puttingDownLoad: {
            sufficientClearance: null,
            clearPersonnelPutting: null,
            slowDownStop: null,
            tiltPlacementLevel: null,
            moveSlowly: null,
            lowerIntoPallet: null,
            lookBeforeBacking: null,
            backStraightOut: null,
            lowerToTraveling: null
        },
        parking: {
            fullyLowerForks: null,
            neutralizeControls: null,
            setBrakes: null,
            turnOffPower: null,
            blockWheels: null,
            parkAuthorized: null
        },
        fuelingBattery: {
            engineOff: null,
            fireExtinguisher: null,
            properPPE: null,
            safeProcedures: null,
            spillsCleaned: null
        },

        qualifiedEquipment: '',
        notQualifiedEquipment: '',

        evaluatorSignature: '',
        evaluatorSignatureEmployeeId: userId,
        evaluatorSignatureEmployeeName: userFullName,

        operatorSignature: '',
        operatorSignatureEmployeeId: '',
        operatorSignatureEmployeeName: '',

        status: STATUS_DRAFT,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    });

    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [operatorModalOpen, setOperatorModalOpen] = useState(false);
    const [evaluatorModalOpen, setEvaluatorModalOpen] = useState(false);
    const [evaluatorSignatureModalOpen, setEvaluatorSignatureModalOpen] = useState(false);
    const [operatorSignatureModalOpen, setOperatorSignatureModalOpen] = useState(false);

    const saveTimer = useRef(null);
    const evaluatorSigPadRef = useRef(null);
    const operatorSigPadRef = useRef(null);

    // Dictation
    const currentFieldRef = useRef(null);
    const [activeField, setActiveField] = useState(null);
    const {
        isSupported: dictationSupported,
        isListening,
        start: startDictation,
        stop: stopDictation
    } = useDictation({
        lang: 'en-US',
        interimResults: false,
        continuous: false,
        safetyTimeoutMs: 15000,
        prime: true,
    });

    // Load data on mount
    useEffect(() => {
        const loadData = async () => {
            const db = await initializeDB();

            // Load employees
            if (db.objectStoreNames.contains(EMPLOYEE_STORE)) {
                const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
                const store = tx.objectStore(EMPLOYEE_STORE);
                const allEmployees = await store.getAll();
                setEmployees(allEmployees || []);
            }

            if (uuid) {
                const existingForm = await operatorPracticalEvaluationService.getFromIndexedDB(uuid);
                if (existingForm) {
                    setForm(existingForm);
                    setSubmitted(existingForm.status === STATUS_SUBMITTED);
                }
            }

            setLoading(false);
        };
        loadData();
    }, [uuid]);

    const handleFieldChange = (field, value) => {
        const updated = { ...form, [field]: value };
        setForm(updated);
        queueSave(updated);
    };

    const handleCheckChange = (section, field, value) => {
        const updated = {
            ...form,
            [section]: { ...form[section], [field]: value }
        };
        setForm(updated);
        queueSave(updated);
    };

    const queueSave = (formData) => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => saveDraft(formData || form), 1000);
    };

    const saveDraft = async (formData) => {
        try {
            const dataToSave = {
                ...(formData || form),
                updatedAt: new Date().toISOString()
            };
            await operatorPracticalEvaluationService.saveToIndexedDB(dataToSave);
        } catch (e) {
            console.error('Error saving draft:', e);
        }
    };

    const handleSelectOperator = (selectedArray) => {
        if (!selectedArray || selectedArray.length === 0) {
            setOperatorModalOpen(false);
            return;
        }
        const person = selectedArray[0];
        const updated = {
            ...form,
            operatorId: person.id || person.ID || '',
            operatorName: person.FullName || person.name || ''
        };
        setForm(updated);
        queueSave(updated);
        setOperatorModalOpen(false);
    };

    const handleSelectEvaluator = (selectedArray) => {
        if (!selectedArray || selectedArray.length === 0) {
            setEvaluatorModalOpen(false);
            return;
        }
        const person = selectedArray[0];
        const updated = {
            ...form,
            evaluatorId: person.id || person.ID || '',
            evaluatorName: person.FullName || person.name || ''
        };
        setForm(updated);
        queueSave(updated);
        setEvaluatorModalOpen(false);
    };

    const handleEvaluatorSignatureAccept = () => {
        if (!evaluatorSigPadRef.current) {
            console.error('Evaluator signature pad ref not found');
            return;
        }

        const dataUrl = evaluatorSigPadRef.current.toDataURL();
        const updated = {
            ...form,
            evaluatorSignature: dataUrl
        };
        setForm(updated);
        setEvaluatorSignatureModalOpen(false);
        queueSave(updated);
    };

    const handleOperatorSignatureAccept = () => {
        if (!operatorSigPadRef.current) {
            console.error('Operator signature pad ref not found');
            return;
        }

        const dataUrl = operatorSigPadRef.current.toDataURL();
        const updated = {
            ...form,
            operatorSignature: dataUrl,
            operatorSignatureEmployeeId: form.operatorId,
            operatorSignatureEmployeeName: form.operatorName
        };
        setForm(updated);
        setOperatorSignatureModalOpen(false);
        queueSave(updated);
    };

    const handleClearEvaluatorSignature = () => {
        const updated = {
            ...form,
            evaluatorSignature: '',
            evaluatorSignatureEmployeeId: '',
            evaluatorSignatureEmployeeName: ''
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleClearOperatorSignature = () => {
        const updated = {
            ...form,
            operatorSignature: '',
            operatorSignatureEmployeeId: '',
            operatorSignatureEmployeeName: ''
        };
        setForm(updated);
        queueSave(updated);
    };

    const validateForm = () => {
        const newErrors = {};
        if (!form.operatorName) newErrors.operatorName = 'Operator is required';
        if (!form.evaluatorName) newErrors.evaluatorName = 'Evaluator is required';
        if (!form.date) newErrors.date = 'Date is required';
        if (!form.equipmentOperated) newErrors.equipmentOperated = 'Equipment Operated is required';
        if (!form.evaluatorSignature) newErrors.evaluatorSignature = 'Evaluator Signature is required';
        if (!form.operatorSignature) newErrors.operatorSignature = 'Operator Signature is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            Swal.fire('Error', 'Please fill in all required fields', 'error');
            return;
        }

        const result = await Swal.fire({
            title: 'Submit Evaluation?',
            text: 'Once submitted, you cannot edit this evaluation.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, submit it!'
        });

        if (result.isConfirmed) {
            try {
                const submittedForm = {
                    ...form,
                    formType: 'operator-practical-evaluation',
                    status: STATUS_SUBMITTED,
                    submittedAt: new Date().toISOString(),
                    submittedBy: userId,
                    sendBy: userId,
                    submittedByName: userFullName,
                    evaluatorSignatureEmployeeId: form.evaluatorSignatureEmployeeId || userId,
                    evaluatorSignatureEmployeeName: form.evaluatorSignatureEmployeeName || userFullName
                };

                let response;
                if (isFromTrainingDue) {
                    // Submit to Training Due endpoint
                    response = await operatorPracticalEvaluationService.submitForTrainingDue(submittedForm);
                } else {
                    // Submit to regular endpoint
                    response = await operatorPracticalEvaluationService.submit(submittedForm);
                }

                if (response.success || response.status === 'ok') {
                    // Delete from IndexedDB after successful submission
                    await operatorPracticalEvaluationService.deleteFromIndexedDB(form.uuid);

                    Swal.fire('Success', 'Evaluation submitted successfully!', 'success');
                    navigate(isFromTrainingDue ? '/training-due' : '/safety/forms/operator-practical-evaluation/list');
                } else {
                    throw new Error(response.message || 'Submission failed');
                }
            } catch (e) {
                console.error('Error submitting:', e);
                Swal.fire('Error', 'Failed to submit evaluation: ' + (e.message || 'Unknown error'), 'error');
            }
        }
    };

    const handleCommentChange = (section, field, value) => {
        const updated = {
            ...form,
            [section]: {
                ...form[section],
                [`${field}_comment`]: value
            }
        };
        setForm(updated);
        queueSave(updated);
    };

    const toggleDictation = async (fieldName) => {
        if (submitted) return;

        const fieldIsActive = isListening && activeField === fieldName;
        if (fieldIsActive) {
            stopDictation();
            setActiveField(null);
            currentFieldRef.current = null;
            return;
        }

        // Stop previous dictation if switching fields
        if (isListening) {
            stopDictation();
        }

        currentFieldRef.current = fieldName;
        setActiveField(fieldName);

        await startDictation({
            onResult: (transcript) => {
                if (!transcript || !currentFieldRef.current) return;

                const field = currentFieldRef.current;

                // Handle text fields
                if (field === 'qualifiedEquipment' || field === 'notQualifiedEquipment') {
                    const currentValue = form[field] || '';
                    const newValue = currentValue + (currentValue ? ' ' : '') + transcript;
                    const updated = {
                        ...form,
                        [field]: newValue
                    };
                    setForm(updated);
                    queueSave(updated);
                }
                // Handle checklist item comments
                else if (field.includes('_')) {
                    const [section, itemField] = field.split('_');
                    const currentComment = form[section]?.[`${itemField}_comment`] || '';
                    const newValue = currentComment + (currentComment ? ' ' : '') + transcript;
                    const updated = {
                        ...form,
                        [section]: {
                            ...form[section],
                            [`${itemField}_comment`]: newValue
                        }
                    };
                    setForm(updated);
                    queueSave(updated);
                }
            },
            onEnd: () => {
                setActiveField(null);
                currentFieldRef.current = null;
            }
        });
    };

    const renderCheckItem = (section, field, label) => {
        const value = form[section][field];
        const comment = form[section][`${field}_comment`] || '';
        const fieldId = `${section}_${field}`;
        const isFieldListening = isListening && activeField === fieldId;

        return (
            <Grid item xs={12} key={field}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    mb: 2,
                    pb: 1.5,
                    borderBottom: '1px solid #e0e0e0'
                }}>
                    <Box sx={{ flex: '0 0 35%', minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                            {label}
                        </Typography>
                    </Box>
                    <Box sx={{ flex: '0 0 200px' }}>
                        <Stack direction="row" spacing={0.5}>
                            <Button
                                variant={value === 'GOOD' ? 'contained' : 'outlined'}
                                color="success"
                                size="small"
                                onClick={() => handleCheckChange(section, field, 'GOOD')}
                                disabled={submitted}
                                sx={{ minWidth: 42, height: 32, fontSize: '0.7rem', padding: '4px 8px' }}
                            >
                                GOOD
                            </Button>
                            <Button
                                variant={value === 'FAIR' ? 'contained' : 'outlined'}
                                color="warning"
                                size="small"
                                onClick={() => handleCheckChange(section, field, 'FAIR')}
                                disabled={submitted}
                                sx={{ minWidth: 42, height: 32, fontSize: '0.7rem', padding: '4px 8px' }}
                            >
                                FAIR
                            </Button>
                            <Button
                                variant={value === 'POOR' ? 'contained' : 'outlined'}
                                color="error"
                                size="small"
                                onClick={() => handleCheckChange(section, field, 'POOR')}
                                disabled={submitted}
                                sx={{ minWidth: 42, height: 32, fontSize: '0.7rem', padding: '4px 8px' }}
                            >
                                POOR
                            </Button>
                            <Button
                                variant={value === 'N/A' ? 'contained' : 'outlined'}
                                color="info"
                                size="small"
                                onClick={() => handleCheckChange(section, field, 'N/A')}
                                disabled={submitted}
                                sx={{ minWidth: 42, height: 32, fontSize: '0.7rem', padding: '4px 8px' }}
                            >
                                N/A
                            </Button>
                        </Stack>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <TextField
                            fullWidth
                            multiline
                            size="small"
                            placeholder="COMMENTS"
                            value={comment}
                            onChange={(e) => handleCommentChange(section, field, e.target.value)}
                            disabled={submitted}
                            InputProps={{
                                endAdornment: !submitted && (
                                    <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.2 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => toggleDictation(fieldId)}
                                            color={isFieldListening ? 'error' : 'primary'}
                                        >
                                            {isFieldListening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{
                                '& .MuiInputBase-input': {
                                    fontSize: '0.75rem',
                                    padding: '3px 6px'
                                },
                                '& .MuiInputBase-root': {
                                    alignItems: 'flex-start',
                                    padding: 0
                                },
                                '& .MuiOutlinedInput-root': {
                                    padding: 0
                                }
                            }}
                        />
                    </Box>
                </Box>
            </Grid>
        );
    };

    if (loading) {
        return (
            <Box sx={{
                height: 'calc(100vh - 60px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            height: 'calc(100vh - 60px)',
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton onClick={() => navigate(isFromTrainingDue ? '/training-due' : '/safety/forms/operator-practical-evaluation/list')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight={600}>OPERATOR PRACTICAL EVALUATION FORM</Typography>
                    </Box>
                    <Chip
                        label={submitted ? 'Submitted' : (isFromTrainingDue ? 'In Progress' : 'Draft')}
                        size="small"
                        sx={{
                            backgroundColor: submitted ? '#4caf50' : (isFromTrainingDue ? '#ff9800' : '#9e9e9e'),
                            color: 'white'
                        }}
                    />
                </Box>
                {isFromTrainingDue && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 6 }}>
                        Training: {courseFromTrainingDue} | Employee: {operatorNameFromTrainingDue}
                    </Typography>
                )}
            </Box>

            {/* Form Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    {/* Instructions */}
                    <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', color: '#1976d2' }}>
                            <strong>Instructions:</strong> Use this checklist during the field session to evaluate operator proficiency.
                        </Typography>
                    </Box>

                    {/* Evaluation Information */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1.5, color: 'primary.main' }}>
                            Evaluation Information
                        </Typography>
                        <Grid container rowSpacing={1} columnSpacing={1}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="EVALUATOR NAME *"
                                    value={form.evaluatorName}
                                    onClick={() => !submitted && setEvaluatorModalOpen(true)}
                                    InputProps={{
                                        readOnly: true,
                                        style: { cursor: submitted ? 'default' : 'pointer' },
                                        endAdornment: !submitted && (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setEvaluatorModalOpen(true)}>
                                                    <PersonSearchIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    disabled={submitted}
                                    error={!!errors.evaluatorName}
                                    helperText={errors.evaluatorName}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="OPERATOR NAME *"
                                    value={form.operatorName}
                                    onClick={() => !submitted && !isFromTrainingDue && setOperatorModalOpen(true)}
                                    InputProps={{
                                        readOnly: true,
                                        style: {
                                            cursor: (submitted || isFromTrainingDue) ? 'default' : 'pointer',
                                            backgroundColor: isFromTrainingDue ? '#f5f5f5' : 'inherit'
                                        },
                                        endAdornment: !submitted && !isFromTrainingDue && (
                                            <InputAdornment position="end">
                                                <IconButton size="small" onClick={() => setOperatorModalOpen(true)}>
                                                    <PersonSearchIcon fontSize="small" />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                    disabled={submitted || isFromTrainingDue}
                                    error={!!errors.operatorName}
                                    helperText={isFromTrainingDue ? 'Pre-filled from Training Due' : errors.operatorName}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="DATE OF EVALUATION *"
                                    type="date"
                                    value={form.date}
                                    onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                    onChange={(e) => handleFieldChange('date', e.target.value)}
                                    disabled={submitted}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{ style: { cursor: submitted ? 'default' : 'pointer' } }}
                                    error={!!errors.date}
                                    helperText={errors.date}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="EQUIPMENT OPERATED *"
                                    value={form.equipmentOperated}
                                    onChange={(e) => handleFieldChange('equipmentOperated', e.target.value)}
                                    disabled={submitted}
                                    error={!!errors.equipmentOperated}
                                    helperText={errors.equipmentOperated}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Pre-use Inspection */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1, color: 'primary.main' }}>
                            Pre-use Inspection
                        </Typography>
                        <Grid container rowSpacing={0.5}>
                            {renderCheckItem('preUseInspection', 'followChecklist', "Follow Operator's Daily Checklist")}
                            {renderCheckItem('preUseInspection', 'lookForDamage', 'Look for Damage')}
                            {renderCheckItem('preUseInspection', 'documentFindings', 'Document all findings on the checklist')}
                        </Grid>
                    </Box>

                    {/* Picking Up The Load */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1, color: 'primary.main' }}>
                            Picking Up The Load
                        </Typography>
                        <Grid container rowSpacing={0.5}>
                            {renderCheckItem('pickingUpTheLoad', 'squareUpCenter', 'Square up on the center of the load')}
                            {renderCheckItem('pickingUpTheLoad', 'stopOneFootFromLoad', 'Stop with the fork tips about 1 foot from the load')}
                            {renderCheckItem('pickingUpTheLoad', 'clearPersonnel', 'Clear personnel from the area near the load')}
                            {renderCheckItem('pickingUpTheLoad', 'levelForks', 'Level the forks; then slowly drive forward until the load contacts the carriage')}
                            {renderCheckItem('pickingUpTheLoad', 'tiltMastBack', 'Tilt the load mast back carefully and smoothly until it is clear')}
                            {renderCheckItem('pickingUpTheLoad', 'tiltBackStabilize', 'Tilt the mast back slightly to stabilize the load')}
                            {renderCheckItem('pickingUpTheLoad', 'lookOverShoulders', 'Look over both shoulders')}
                            {renderCheckItem('pickingUpTheLoad', 'lowerToTravelHeight', 'After out and stopped, lower the load to travel height')}
                        </Grid>
                    </Box>

                    {/* Traveling */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1, color: 'primary.main' }}>
                            Traveling
                        </Typography>
                        <Grid container rowSpacing={0.5}>
                            {renderCheckItem('traveling', 'doNotRaiseLower', 'Do not raise or lower the load and forks while traveling')}
                            {renderCheckItem('traveling', 'maintainSpeed', 'Maintain a safe speed')}
                            {renderCheckItem('traveling', 'observeRules', 'Observe all traffic rules, warning signs, floor load limits and overhead clearances')}
                            {renderCheckItem('traveling', 'keepArmsLegs', 'Keep arms and legs inside the forklift')}
                            {renderCheckItem('traveling', 'followDistance', 'Follow other vehicles at safe distance')}
                            {renderCheckItem('traveling', 'slowDownCornering', 'Slow down when cornering')}
                            {renderCheckItem('traveling', 'useHorn', 'Use the horn to alert others')}
                            {renderCheckItem('traveling', 'travelFacingUphill', 'Travel with the load facing uphill while on a ramp or incline')}
                            {renderCheckItem('traveling', 'stopSmoothly', 'Stop smoothly')}
                        </Grid>
                    </Box>

                    {/* Putting Down A Load */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1, color: 'primary.main' }}>
                            Putting Down A Load
                        </Typography>
                        <Grid container rowSpacing={0.5}>
                            {renderCheckItem('puttingDownLoad', 'sufficientClearance', 'Make sure there is sufficient clearance for the load')}
                            {renderCheckItem('puttingDownLoad', 'clearPersonnelPutting', 'Clear personnel from the area near the load')}
                            {renderCheckItem('puttingDownLoad', 'slowDownStop', 'Slow down to the location; then stop about 1 foot away')}
                            {renderCheckItem('puttingDownLoad', 'tiltPlacementLevel', 'Tilt the load to placement level')}
                            {renderCheckItem('puttingDownLoad', 'moveSlowly', 'Move slowly forward')}
                            {renderCheckItem('puttingDownLoad', 'lowerIntoPallet', 'When the load is on a pallet, lower it into position and tilt the load mast back further')}
                            {renderCheckItem('puttingDownLoad', 'lookBeforeBacking', 'Look over both shoulders before backing out')}
                            {renderCheckItem('puttingDownLoad', 'backStraightOut', 'Back straight out until the forks are cleared')}
                            {renderCheckItem('puttingDownLoad', 'lowerToTraveling', 'Lower the forks to traveling position')}
                        </Grid>
                    </Box>

                    {/* Parking */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1, color: 'primary.main' }}>
                            Parking
                        </Typography>
                        <Grid container rowSpacing={0.5}>
                            {renderCheckItem('parking', 'fullyLowerForks', 'Fully lower the forks')}
                            {renderCheckItem('parking', 'neutralizeControls', 'Neutralize the controls')}
                            {renderCheckItem('parking', 'setBrakes', 'Set the brakes')}
                            {renderCheckItem('parking', 'turnOffPower', 'Turn off the power')}
                            {renderCheckItem('parking', 'blockWheels', 'If parked on a slope, block the wheels')}
                            {renderCheckItem('parking', 'parkAuthorized', 'Park only in authorized areas')}
                        </Grid>
                    </Box>

                    {/* Fueling and Battery Recharging */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1, color: 'primary.main' }}>
                            Fueling and Battery Recharging
                        </Typography>
                        <Grid container rowSpacing={0.5}>
                            {renderCheckItem('fuelingBattery', 'engineOff', 'Engine off')}
                            {renderCheckItem('fuelingBattery', 'fireExtinguisher', 'Fire extinguisher nearby')}
                            {renderCheckItem('fuelingBattery', 'properPPE', 'Proper personal protective equipment worn')}
                            {renderCheckItem('fuelingBattery', 'safeProcedures', 'Safe fueling and battery recharging procedures followed')}
                            {renderCheckItem('fuelingBattery', 'spillsCleaned', 'Spills cleaned up immediately')}
                        </Grid>
                    </Box>

                    {/* Evaluation Summary */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25 }}>Evaluation Summary</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        size="small"
                                        label="Qualified Equipment"
                                        value={form.qualifiedEquipment}
                                        onChange={(e) => handleFieldChange('qualifiedEquipment', e.target.value)}
                                        disabled={submitted}
                                        placeholder="Based on my evaluation, the operator has successfully completed the evaluation and is qualified to operate the following equipment:"
                                        InputProps={{
                                            endAdornment: !submitted && (
                                                <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleDictation('qualifiedEquipment')}
                                                        color={isListening && activeField === 'qualifiedEquipment' ? 'error' : 'primary'}
                                                    >
                                                        {isListening && activeField === 'qualifiedEquipment' ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={2}
                                        size="small"
                                        label="Not Qualified Equipment"
                                        value={form.notQualifiedEquipment}
                                        onChange={(e) => handleFieldChange('notQualifiedEquipment', e.target.value)}
                                        disabled={submitted}
                                        placeholder="Based on my evaluation, the operator has not demonstrated competence in operating the following equipment:"
                                        InputProps={{
                                            endAdornment: !submitted && (
                                                <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleDictation('notQualifiedEquipment')}
                                                        color={isListening && activeField === 'notQualifiedEquipment' ? 'error' : 'primary'}
                                                    >
                                                        {isListening && activeField === 'notQualifiedEquipment' ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Signatures */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25 }}>Signatures</Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Evaluator Signature *</Typography>
                                    {form.evaluatorSignature ? (
                                        <Box>
                                            <Box position="relative" display="inline-block">
                                                <img
                                                    src={form.evaluatorSignature}
                                                    alt="Evaluator Signature"
                                                    style={{
                                                        maxWidth: 200,
                                                        height: 60,
                                                        border: '1px solid #ddd',
                                                        borderRadius: 4,
                                                        cursor: submitted ? 'default' : 'pointer'
                                                    }}
                                                    onClick={() => {
                                                        if (!submitted) {
                                                            setEvaluatorSignatureModalOpen(true);
                                                        }
                                                    }}
                                                    title={submitted ? '' : 'Click to edit signature'}
                                                />
                                                {!submitted && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleClearEvaluatorSignature}
                                                        sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                    >
                                                        <ClearIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                Signed by: {form.evaluatorName}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Button
                                            variant="outlined"
                                            startIcon={<DrawIcon />}
                                            onClick={() => setEvaluatorSignatureModalOpen(true)}
                                            disabled={submitted}
                                            size="small"
                                        >
                                            Add Signature
                                        </Button>
                                    )}
                                    {errors.evaluatorSignature && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                            {errors.evaluatorSignature}
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Operator Signature *</Typography>
                                    {form.operatorSignature ? (
                                        <Box>
                                            <Box position="relative" display="inline-block">
                                                <img
                                                    src={form.operatorSignature}
                                                    alt="Operator Signature"
                                                    style={{
                                                        maxWidth: 200,
                                                        height: 60,
                                                        border: '1px solid #ddd',
                                                        borderRadius: 4,
                                                        cursor: submitted ? 'default' : 'pointer'
                                                    }}
                                                    onClick={() => {
                                                        if (!submitted) {
                                                            setOperatorSignatureModalOpen(true);
                                                        }
                                                    }}
                                                    title={submitted ? '' : 'Click to edit signature'}
                                                />
                                                {!submitted && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleClearOperatorSignature}
                                                        sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                    >
                                                        <ClearIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                Signed by: {form.operatorName}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Button
                                            variant="outlined"
                                            startIcon={<DrawIcon />}
                                            onClick={() => setOperatorSignatureModalOpen(true)}
                                            disabled={submitted}
                                            size="small"
                                        >
                                            Add Signature
                                        </Button>
                                    )}
                                    {errors.operatorSignature && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                            {errors.operatorSignature}
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ backgroundColor: 'white', borderTop: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => navigate(isFromTrainingDue ? '/training-due' : '/safety/forms/operator-practical-evaluation/list')}
                    >
                        Cancel
                    </Button>
                    {!submitted && (
                        <Button
                            variant="contained"
                            startIcon={<SendIcon />}
                            onClick={handleSubmit}
                            sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
                        >
                            Submit
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Modals */}
            {operatorModalOpen && (
                <SelectEmployeesAndTempsModal
                    open={operatorModalOpen}
                    onClose={() => setOperatorModalOpen(false)}
                    onSelect={handleSelectOperator}
                    title="Select Operator"
                    singleSelect={true}
                    alreadySelected={[]}
                />
            )}

            {evaluatorModalOpen && (
                <SelectEmployeesAndTempsModal
                    open={evaluatorModalOpen}
                    onClose={() => setEvaluatorModalOpen(false)}
                    onSelect={handleSelectEvaluator}
                    title="Select Evaluator"
                    singleSelect={true}
                    alreadySelected={[]}
                />
            )}

            {evaluatorSignatureModalOpen && (
                <SignatureModalWithAuto
                    open={evaluatorSignatureModalOpen}
                    onClose={() => setEvaluatorSignatureModalOpen(false)}
                    sigPadRef={evaluatorSigPadRef}
                    employeeName={form.evaluatorSignatureEmployeeName}
                    onAccept={handleEvaluatorSignatureAccept}
                    existingSignature={form.evaluatorSignature}
                />
            )}

            {operatorSignatureModalOpen && (
                <SignatureModalWithAuto
                    open={operatorSignatureModalOpen}
                    onClose={() => setOperatorSignatureModalOpen(false)}
                    sigPadRef={operatorSigPadRef}
                    employeeName={form.operatorName}
                    onAccept={handleOperatorSignatureAccept}
                    existingSignature={form.operatorSignature}
                />
            )}
        </Box>
    );
};

export default OperatorPracticalEvaluationForm;
